/**
 *
 * Header
 *
 */
import React, { memo, useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { createAbsoluteUrl } from "utils/constants";
import { selectUser } from "app/slice/selectors";
import { exactPaths } from "../index";
import { HelpContactBar } from "./help-contact-bar";
import { MobileMyAccount } from "./mobile-my-account";
import { HamburgerMenu } from "./hamburger-menu";
import { ActivateDialog } from "./activate-dialog";
import { PromoBanner } from "./promo-banner";
import { MaintenanceBanner } from "./maintenance-banner";
import { MainNavigation } from "./main-navigation";
import { clearPurchaseFlowData } from "utils/purchase-flow-storage";
import { useGlobalSlice } from "app/slice";

import { Button } from "app/components/ui/button";

const partialPaths = [
  "network-plan",
  "selection",
  "phone-type",
  "number-type",
  "number-information",
  "activation/fixed-phone",
  "activation/preloaded-sim",
  "checkout",
];

interface Props {
  isHeaderRelative?: boolean;
  showAppDownload: boolean;
  handleDismiss: (permanent?: boolean) => void;
  handleOpenApp: () => void;
  // showPromoBanner: boolean;
  // setShowPromoBanner: (show: boolean) => void;
  promoProductId?: string;
  showMaintenanceBanner: boolean;
  setShowMaintenanceBanner: (show: boolean) => void;
}

export const Header = memo(
  ({
    isHeaderRelative = false,
    showAppDownload,
    handleDismiss,
    handleOpenApp,
    // showPromoBanner,
    // setShowPromoBanner,
    promoProductId,
    showMaintenanceBanner,
    setShowMaintenanceBanner,
  }: Props) => {
    const user = useSelector(selectUser);
    const agent = {
      firstName: "John",
      lastName: "Smith",
    };
    const location = useLocation();
    const { pathname } = location;
    const [showHelpBar, setShowHelpBar] = useState(false);
    const [showAppDownloadBanner, setShowAppDownloadBanner] =
      useState(showAppDownload);
    const [activateDialogOpen, setActivateDialogOpen] = useState(false);
    const [lastScrollY, setLastScrollY] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const [scrollY, setScrollY] = useState(0);

    const isMyAccountPage = pathname.startsWith("/my-account");
    const isAgentPage = pathname.startsWith("/agent");
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { actions } = useGlobalSlice();

    const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      // Check if we're currently in a purchase flow
      const purchaseFlowRoutes = [
        "/network-plan",
        "/multi-network-selection",
        "/single-network-selection",
        "/multi-network-phone-type",
        "/single-network-phone-type",
        "/multi-network-number-type",
        "/single-network-number-type",
        "/checkout",
      ];

      const isInPurchaseFlow = purchaseFlowRoutes.some(
        (route) => pathname === route || pathname.startsWith(route + "/")
      );

      if (isInPurchaseFlow) {
        // Clear purchase flow data before navigating to external URL
        clearPurchaseFlowData();
        dispatch(actions.setPlanData({ planData: null }));
        dispatch(actions.setLines({ numberOfLines: null }));
        dispatch(actions.collectCheckAvailabilityDetails({ CAD: null }));
        dispatch(actions.setCompatibilityData({ CD: null }));
        dispatch(actions.setCompatibilitiesData({ CDS: [] }));
        dispatch(actions.setIsPorting({ isPorting: false }));
        dispatch(actions.setPortRequestSubmitted(false));
        // Set flag to indicate we've exited purchase flow
        sessionStorage.setItem("purchaseFlowExited", "true");
      }
    };

    const calculateShadowOpacity = () => {
      const maxOpacity = 0.2;
      const shadowThreshold = 200;

      // Ensure shadow is hidden when at the very top of the page
      if (scrollY <= 0) return 0;

      // Ensure shadow is at maximum when scrolled past threshold
      if (scrollY >= shadowThreshold) return maxOpacity;

      // Calculate progressive shadow opacity
      const baseOpacity = 0.01;
      const additionalOpacity =
        (scrollY / shadowThreshold) * (maxOpacity - baseOpacity);
      return Math.min(baseOpacity + additionalOpacity, maxOpacity);
    };

    const shadowOpacity = calculateShadowOpacity();

    const shouldShowNav = !exactPaths.includes(location.pathname);

    const shouldShowQuestionBtn = location.pathname.includes("network-plan");

    const shouldShowHelpBtn = partialPaths.some((path) =>
      location.pathname.includes(path)
    );
    // !location.pathname.includes("/checkout");

    const shouldShowContactAndStartBtn =
      location.pathname.includes("broadband-facts");

    useEffect(() => {
      const checkScreenSize = () => {
        setIsMobile(window.innerWidth < 640);
      };

      checkScreenSize();
      window.addEventListener("resize", checkScreenSize);

      // Initialize scroll state
      setScrollY(window.scrollY);
      setLastScrollY(window.scrollY);

      return () => window.removeEventListener("resize", checkScreenSize);
    }, []);

    useEffect(() => {
      // Show help bar on all specified routes
      if (shouldShowNav) {
        setShowHelpBar(true);
        return;
      } else {
        setShowHelpBar(false);
        return;
      }
    }, [location.pathname, shouldShowNav]);

    useEffect(() => {
      // Reset scroll state when location changes
      setScrollY(0);
      setLastScrollY(0);
    }, [location.pathname]);

    useEffect(() => {
      // Update app download banner visibility when prop changes
      setShowAppDownloadBanner(showAppDownload);
    }, [showAppDownload]);

    useEffect(() => {
      const handleScroll = () => {
        const currentScrollY = window.scrollY;

        // Update shadow scroll state (always update for shadow calculation)
        setScrollY(currentScrollY);

        // Handle help bar scroll behavior (only when shouldShowNav is true)
        if (shouldShowNav) {
          if (currentScrollY <= 25) {
            setShowHelpBar(true);
          } else {
            if (currentScrollY > lastScrollY && currentScrollY > 50) {
              setShowHelpBar(false);
            } else if (currentScrollY < lastScrollY) {
              setShowHelpBar(true);
            }
          }
        }

        // Handle app download banner scroll behavior (only when shouldShowNav is true)
        if (shouldShowNav && showAppDownload) {
          if (currentScrollY <= 25) {
            setShowAppDownloadBanner(true);
          } else {
            if (currentScrollY > lastScrollY && currentScrollY > 50) {
              setShowAppDownloadBanner(false);
            } else if (currentScrollY < lastScrollY) {
              setShowAppDownloadBanner(true);
            }
          }
        }

        setLastScrollY(currentScrollY);
      };

      window.addEventListener("scroll", handleScroll, { passive: true });

      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }, [location.pathname, lastScrollY, shouldShowNav, showAppDownload]);

    return (
      <React.Fragment>
        {shouldShowNav && !isAgentPage && (
          <>
            {/* <div className={`flex justify-between items-center gap-4 bg-[#F3F3F3] sm:hidden transition-all duration-200 ease-in-out ${showAppDownloadBanner ? 'h-auto p-2 pr-3 opacity-100' : 'max-h-0 opacity-0'}`}>
						<div className="flex [@media(max-width:400px)]:gap-1.5 gap-2.5 items-center">
							<Button 
								type="button"
								variant="ghost"
								className="size-3.5"
								onClick={() => handleDismiss(false)}
								onKeyDown={(e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										handleDismiss(false);
									}
								}}
								aria-label="Close banner"
							>
								<img src="/assets/close.svg" alt="close" className="size-full" />
							</Button>
							<div className="overflow-hidden bg-[#FE4D01] rounded-lg size-8">
								<div className="w-16 h-8 pl-0.5">
									<img src="/assets/vle-white.svg" alt="vle" className="size-full" />
								</div>
							</div>
							<div className="">
								<h5 className="font-mobile text-black [@media(max-width:375px)]:text-[0.78125rem] text-sm font-semibold leading-4 mb-1">
									Fast, Easy and Convenient
								</h5>
								<p className="text-black [@media(max-width:400px)]:text-[0.65rem] text-xs font-medium leading-[0.875rem]">
									Get our APP for the best experience
								</p>
							</div>
						</div>
						<Button
							type="button"
							variant="outline"
							className="px-3 h-8 uppercase rounded-full max-w-fit [@media(max-width:400px)]:text-xs"
							onClick={handleOpenApp}
						>
							Open App
						</Button>
					</div> */}
            {/* <PromoBanner
              isVisible={showPromoBanner}
              onClose={() => setShowPromoBanner(false)}
              productId={promoProductId}
            /> */}
            <MaintenanceBanner isVisible={showMaintenanceBanner} onClose={() => setShowMaintenanceBanner(false)} />
            <div
              className={`transition-all duration-200 ease-in-out ${
                showHelpBar
                  ? "max-h-8 sm:max-h-10 lg:max-h-[3.125rem] opacity-100"
                  : "max-h-0 opacity-0"
              }`}
            >
              <HelpContactBar />
            </div>
          </>
        )}
        <div
          className={
            isHeaderRelative
              ? "relative z-10 w-full bg-white"
              : "fixed z-10 w-full bg-white"
          }
          style={
            isHeaderRelative
              ? undefined
              : {
                  boxShadow: !isMobile
                    ? `0 2px 6px rgba(0, 0, 0, ${shadowOpacity})`
                    : ``,
                  transition: !isMobile ? "box-shadow 0.1s ease-out" : "",
                }
          }
        >
          <div
            className={`max-w-[86rem] flex justify-between items-center px-5 sm:px-6 md:px-7 lg:px-5 border-b border-b-[#F2EEE9] sm:border-b-0 mx-auto 
						${
              isAgentPage
                ? "h-14 sm:h-[4.375rem] lg:h-[5.3125rem]"
                : "h-16 sm:h-20 lg:h-24"
            }
					`}
          >
            <div className="flex items-center gap-5 xl:gap-[3.85rem]">
              <a
                href={
                  isAgentPage
                    ? "/agent/customer-dashboard"
                    : createAbsoluteUrl()
                }
                target="_self"
                rel="noopener noreferrer"
                onClick={handleLogoClick}
                className="relative flex-shrink-0 [@media(max-width:375px)]:w-[4.5rem] [@media(max-width:375px)]:h-8 w-24 h-10 sm:w-[6.1875rem] sm:h-[2.93769rem]"
              >
                <img
                  src={isAgentPage ? "/assets/vle.svg" : "/assets/vle-logo.svg"}
                  className="w-full h-full"
                  alt="vle mobile"
                />
                {/* w-[4.08125rem] h-[1.93769rem] */}
                {isAgentPage && (
                  <span className="inline-block overflow-hidden absolute left-1/2 top-1/2 text-base font-bold leading-5 text-black whitespace-nowrap -translate-y-1/2 font-mobile [@media(max-width:435px)]:text-xs sm:text-xl md:text-[1.375rem] lg:text-2xl sm:leading-6 lg:leading-7 text-ellipsis">
                    Agent <br className="[@media(min-width:435px)]:hidden" />{" "}
                    Dashboard
                  </span>
                )}
              </a>
              {shouldShowNav && !isAgentPage && <MainNavigation />}
            </div>
            {isAgentPage ? (
              <div className="flex gap-2 items-center sm:gap-3 md:gap-4 lg:gap-5">
                <span className="inline-block max-w-28 sm:max-w-48 pr-[0.3125rem] text-[#1D1D1B] text-sm sm:text-[0.925rem] lg:text-base font-semibold leading-[1.575rem] whitespace-nowrap text-ellipsis overflow-hidden">
                  Hi, {agent?.firstName} {agent?.lastName}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  className="gap-1 bg-[#1D1D1B] hover:bg-black min-w-24 sm:min-w-28 lg:min-w-[7.5rem] h-10 sm:h-11 lg:h-12 px-3 py-2 sm:px-[1.375rem] sm:py-[0.685rem] rounded-[6.25rem] text-white hover:text-white text-xs sm:text-sm font-semibold leading-[1.575rem] hover:shadow-md"
                >
                  Logout
                  <img
                    src="/assets/logout.svg"
                    alt="Logout"
                    className="flex-shrink-0 size-4 sm:size-[1.125rem] lg:size-5"
                  />
                </Button>
              </div>
            ) : // : shouldShowQuestionBtn ? (
            // <Button
            // 	type="button"
            // 	variant="ghost"
            // 	className="gap-[0.3125rem] sm:gap-1.5 lg:gap-[0.44rem] bg-[#1D1D1B] hover:bg-black max-w-[11.25rem] h-10 sm:h-11 lg:h-12 pl-2.5 pr-3 sm:pl-3.5 sm:pr-4 lg:pl-4 lg:pr-[1.125rem] border border-[#F2EEE9] rounded-[6.25rem] hover:shadow-md text-white hover:text-white text-xs sm:text-sm font-semibold leading-[1.575rem]"
            // >
            // 	<img
            // 		src="/assets/messages.webp"
            // 		alt="Messages"
            // 		className="flex-shrink-0 size-4 sm:size-[1.125rem] lg:size-5"
            // 	/>
            // 	Have a question?
            // </Button>
            // )
            shouldShowHelpBtn ? (
              <Button
                type="button"
                variant="link"
                className="gap-[0.3125rem] sm:gap-1.5 lg:gap-[0.44rem] mr-1.5 sm:mr-2 text-black text-xs sm:text-sm font-semibold leading-[1.575rem]"
                onClick={() => (window.location.href = "tel:18448536624")}
              >
                <img
                  src="/assets/call-calling.svg"
                  alt="Messages"
                  className="flex-shrink-0 size-4 sm:size-[1.125rem] lg:size-5"
                />
                Need Help? 844-VLEMOBILE
              </Button>
            ) : (
              // : shouldShowContactAndStartBtn ? (
              // 	<div className="flex items-center gap-4 sm:gap-6 lg:gap-8 2xl:gap-[2.17rem]">
              // 		<Button
              // 			type="button"
              // 			variant="link"
              // 			className="text-[#464646] text-xs sm:text-sm font-semibold leading-4 sm:leading-5"
              // 			onClick={() => window.location.href = "tel:18448536624"}
              // 		>
              // 			Contact Us 📞 844-VLEMOBILE
              // 		</Button>
              // 		<Button
              // 			type="button"
              // 			variant="ghost"
              // 			className="hidden lg:inline-flex bg-[#1D1D1B] hover:bg-black min-w-[7.5rem] sm:min-w-32 lg:min-w-[9.3125rem] h-10 sm:h-11 lg:h-12 px-3 py-2 sm:p-[0.875rem_1.125rem] rounded-[6.25rem] mr-1.5 sm:mr-2 text-white hover:text-white text-xs sm:text-sm font-semibold leading-[1.575rem] hover:shadow-md"
              // 			onClick={() => navigate('/network-plan')}
              // 		>
              // 			Get started Free
              // 		</Button>
              // 	</div>
              // )
              <div className="flex items-center gap-2.5 lg:gap-3.5">
                {/* {!user ? (
										<Button
											type="button"
											variant="ghost"
											className="hidden lg:inline-flex bg-transparent hover:bg-[#FAFAFA] min-w-[4.65rem] sm:min-w-20 lg:min-w-[6.3125rem] h-10 sm:h-11 lg:h-12 px-3 py-2 sm:p-[0.875rem_1.125rem] border border-[#F2EEE9] rounded-[6.25rem] text-[#1D1D1B] hover:text-[#1D1D1B] text-xs sm:text-sm font-semibold leading-[1.575rem] hover:shadow-sm"
											onClick={() => navigate('/login')}
										>
											Log in
										</Button>
									) : (
										<span className="hidden lg:inline-block max-w-36 lg:max-w-40 xl:max-w-48 pr-[0.3125rem] xl:pr-1.5 text-[#1D1D1B] text-sm sm:text-[0.925rem] xl:text-base font-semibold leading-[1.575rem] whitespace-nowrap text-ellipsis overflow-hidden">
											Hi, {user?.firstName} {user?.lastName}
										</span>
									)} */}
                {!user ? (
                  <Button
                    type="button"
                    variant="ghost"
                    className="hidden lg:inline-flex bg-[#1D1D1B] hover:bg-black min-w-[7.5rem] sm:min-w-32 lg:min-w-[9.3125rem] h-10 sm:h-11 lg:h-12 px-3 py-2 sm:p-[0.875rem_1.125rem] border border-[#F2EEE9] rounded-[6.25rem] text-white hover:text-white text-xs sm:text-sm font-semibold leading-[1.575rem] hover:shadow-md"
                    onClick={() => navigate("/network-plan")}
                  >
                    {/* Start for $1 -&gt; */}
                    Get Started -&gt;
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    className="hidden lg:inline-flex gap-[0.3125rem] sm:gap-1.5 lg:gap-[0.44rem] bg-[#1D1D1B] hover:bg-black min-w-[7.75rem] sm:min-w-32 lg:min-w-[9.3125rem] h-10 sm:h-11 lg:h-12 pl-2.5 pr-3 py-2 sm:p-[0.875rem_1.125rem_0.875rem_1rem] border border-[#F2EEE9] rounded-[6.25rem] text-white hover:text-white text-xs sm:text-sm font-semibold leading-[1.575rem] hover:shadow-md"
                    // onClick={() => navigate(isMyAccountPage ? "/" : "/my-account/dashboard")}
                    onClick={() =>
                      isMyAccountPage
                        ? window.open(
                            createAbsoluteUrl(),
                            "_self",
                            "noopener,noreferrer"
                          )
                        : navigate("/my-account/dashboard")
                    }
                  >
                    {isMyAccountPage ? (
                      <>&lt;- Back to Home</>
                    ) : (
                      <>
                        <img
                          src="/assets/profile-circle.webp"
                          alt="Profile"
                          className="flex-shrink-0 size-4 sm:size-[1.125rem] lg:size-5"
                        />
                        My Account
                      </>
                    )}
                  </Button>
                )}
                {/* <Button
										type="button"
										variant="ghost"
										className="lg:hidden bg-transparent hover:bg-[#F2EEE9] min-w-[7.25rem] h-7 p-[0.44rem] border border-[#00000014] rounded-lg text-black hover:text-black text-xs font-medium leading-[0.875rem] hover:shadow-md"
										onClick={() => window.open('https://appeningxyz-my.sharepoint.com/:f:/g/personal/nikunj_s_appening_xyz/EqneHGUQG7dIqle7AYLmDzgB1LtXD0phpyf4ouGFkejiMg?e=OzftmK', '_blank', "noopener,noreferrer")}
									>
										<img
											src="/assets/download-icon.svg"
											alt="Download"
											className="shrink-0 size-3.5 mr-1"
										/>
										Download App
									</Button> */}
                <div className="flex items-center lg:hidden">
                  <MobileMyAccount />
                  <HamburgerMenu
                    shouldApplyTop={showHelpBar}
                    onActivate={() => setActivateDialogOpen(true)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <ActivateDialog
          isOpen={activateDialogOpen}
          onOpenChange={setActivateDialogOpen}
          hideTrigger={true}
        />
      </React.Fragment>
    );
  }
);
